import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { sendContactNotificationEmail } from "email/contactNotification";
import { captureServerException } from "@/lib/sentry";
import { _submitContact } from "instantdb/server/contactSubmissions";

// Define the input schema using Zod - adjust as needed
const submitContactInputSchema = z.object({
  // Add expected input fields here, e.g., email, message
  userId: z.optional(z.string()),
  email: z.string().email(),
  description: z.string().min(1),
});

export const submitContact = createServerFn({
  method: "POST",
})
  .validator(submitContactInputSchema)
  .handler(async (ctx) => {
    // Destructure validated input from ctx.data
    const { email, description, userId } = ctx.data;

    try {
      await _submitContact({ email, description, userId: userId ?? "" });

      // Send notification email (only in PROD)
      if (import.meta.env.PROD && import.meta.env.VITE_TEST !== "true") {
        await sendContactNotificationEmail({
          fromEmail: email,
          description: description,
          userId: userId,
        });
      }

      // Return a success response
      return { success: true, message: "Contact form submitted successfully." };
    } catch (error: unknown) {
      // Use the safe server-side capture function
      captureServerException(error, {
        tags: {
          endpoint: "submit-contact-server-fn",
          userId: userId,
          email: email,
        },
      });

      // Re-throw the error to be handled by the client or a higher-level error boundary
      // Log less detail in production
      throw new Error(
        !import.meta.env.PROD
          ? error instanceof Error
            ? error.message
            : String(error)
          : "Failed to submit contact form",
      );
    }
  });
